import Modal from '@/components/modal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { getFileIconFromUrl } from '@/components/ui/file-icon'
import { ExternalLink, FileText, Image as ImageIcon, LoaderCircle, X, ZoomIn, ZoomOut } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer'

interface URLViewerModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  url: string
  isFileViewerNestedModal?: boolean
  title?: string
}

// Helper function to extract file extension from URL (handles query parameters)
const getFileExtensionFromUrl = (url: string): string => {
  try {
    const urlWithoutQuery = url.split('?')[0] // Remove query parameters
    const parts = urlWithoutQuery.split('/')
    const filename = parts[parts.length - 1]
    const extension = filename.split('.').pop()?.toLowerCase() || ''
    return extension
  } catch {
    return ''
  }
}

// Helper function to extract filename from URL
const getFilenameFromUrl = (url: string): string => {
  try {
    const urlWithoutQuery = url.split('?')[0]
    const parts = urlWithoutQuery.split('/')
    return parts[parts.length - 1] || 'File'
  } catch {
    return 'File'
  }
}

const URLViewerModal = ({ open, setOpen, url, isFileViewerNestedModal, title }: URLViewerModalProps) => {
  const [loading, setLoading] = useState(false)
  const [fileContent, setFileContent] = useState<string | null>(null)
  const [imageScale, setImageScale] = useState(1)

  // Extract file information from URL
  const fileExtension = useMemo(() => getFileExtensionFromUrl(url), [url])
  const fileName = useMemo(() => getFilenameFromUrl(url), [url])
  const fileIconElement = useMemo(() => getFileIconFromUrl(url), [url])

  // File type detection based on extension
  const isImage = useMemo(
    () => ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'].includes(fileExtension),
    [fileExtension]
  )
  const isPdf = useMemo(() => fileExtension === 'pdf', [fileExtension])
  const isTextFile = useMemo(
    () => ['csv', 'json', 'xml', 'html', 'htm', 'txt', 'md', 'log', 'yaml', 'yml'].includes(fileExtension),
    [fileExtension]
  )
  const isWordDoc = useMemo(() => ['docx', 'doc'].includes(fileExtension), [fileExtension])
  const isExcelDoc = useMemo(() => ['xlsx', 'xls', 'xlsm'].includes(fileExtension), [fileExtension])
  const isPowerPointDoc = useMemo(() => ['pptx', 'ppt'].includes(fileExtension), [fileExtension])
  const isHtmlDoc = useMemo(() => ['html', 'htm'].includes(fileExtension), [fileExtension])
  const isOfficeDoc = useMemo(
    () => isWordDoc || isExcelDoc || isPowerPointDoc || isHtmlDoc,
    [isWordDoc, isExcelDoc, isPowerPointDoc, isHtmlDoc]
  )

  // Get MIME type based on extension
  const mimeType = useMemo(() => {
    const mimeTypes: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      bmp: 'image/bmp',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      doc: 'application/msword',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ppt: 'application/vnd.ms-powerpoint',
      html: 'text/html',
      htm: 'text/html',
      txt: 'text/plain',
      csv: 'text/csv',
      json: 'application/json',
      xml: 'application/xml',
      md: 'text/markdown',
    }
    return mimeTypes[fileExtension] || 'application/octet-stream'
  }, [fileExtension])

  const loadFileContent = useCallback(async () => {
    if (!url) return

    try {
      setLoading(true)

      // Load file content for text-based files
      if (isTextFile && !isPdf) {
        try {
          const contentResponse = await fetch(url)
          if (contentResponse.ok) {
            const content = await contentResponse.text()
            setFileContent(content)
          }
        } catch (error) {
          console.warn('Failed to load file content:', error)
        }
      }
    } catch (error) {
      console.error('Error loading file:', error)
    } finally {
      setLoading(false)
    }
  }, [url, isTextFile, isPdf])

  useEffect(() => {
    if (open && url) loadFileContent()
  }, [open, url, loadFileContent])

  const handleOpenInNewTab = () => {
    if (url) window.open(url, '_blank')
  }

  const resetZoom = () => setImageScale(1)
  const zoomIn = () => setImageScale((prev) => Math.min(prev + 0.25, 3))
  const zoomOut = () => setImageScale((prev) => Math.max(prev - 0.25, 0.25))

  const renderImageViewer = () => (
    <div className='relative w-full h-full flex items-center justify-center bg-black/5 dark:bg-white/5 rounded-lg'>
      {loading ? (
        <div className='flex flex-col justify-center items-center space-y-4 h-100 '>
          <LoaderCircle className='h-8 w-8 animate-spin' />
          <p className='text-sm text-muted-foreground'>Loading image...</p>
        </div>
      ) : url ? (
        <div className='relative max-w-full max-h-full overflow-auto'>
          <img
            src={url}
            alt={fileName}
            loading='lazy'
            decoding='async'
            className='max-w-full max-h-full object-contain'
            style={{ transform: `scale(${imageScale})` }}
            onError={() => setLoading(false)}
          />
        </div>
      ) : (
        <div className='flex flex-col items-center space-y-4'>
          <ImageIcon className='h-12 w-12 text-muted-foreground' />
          <p className='text-sm text-muted-foreground'>Failed to load image</p>
        </div>
      )}
    </div>
  )

  const renderPdfViewer = () => (
    <div className='w-full h-full'>
      {loading ? (
        <div className='flex flex-col justify-center items-center space-y-4 h-100'>
          <LoaderCircle className='h-8 w-8 animate-spin' />
          <p className='text-sm text-muted-foreground'>Loading PDF...</p>
        </div>
      ) : url ? (
        <div className='flex flex-col items-center h-[calc(80vh-100px)]'>
          <div className='w-full h-full'>
            <iframe
              src={`${url}#toolbar=1&navpanes=1&scrollbar=1`}
              className='w-full h-full border rounded-lg'
              title={fileName}
              style={{ minHeight: '600px' }}
            />
          </div>
        </div>
      ) : (
        <div className='flex flex-col items-center justify-center h-64 space-y-4'>
          <FileText className='h-12 w-12 text-muted-foreground' />
          <p className='text-sm text-muted-foreground'>Failed to load PDF</p>
          <Button type='button' variant='outline' onClick={handleOpenInNewTab}>
            <ExternalLink className='h-4 w-4 mr-2' />
            Open in New Tab
          </Button>
        </div>
      )}
    </div>
  )

  const renderTextFileViewer = () => (
    <div className='w-full h-[calc(80vh-100px)]'>
      {loading ? (
        <div className='flex flex-col justify-center items-center space-y-4 h-100 '>
          <LoaderCircle className='h-8 w-8 animate-spin' />
          <p className='text-sm text-muted-foreground'>Loading file content...</p>
        </div>
      ) : fileContent ? (
        <div className='h-[calc(80vh-100px)] w-full overflow-y-auto'>
          <Card>
            <CardContent className='p-4'>
              <pre className='text-sm font-mono whitespace-pre-wrap break-words'>{fileContent}</pre>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className='flex flex-col items-center justify-center h-64 space-y-4'>
          <FileText className='h-12 w-12 text-muted-foreground' />
          <p className='text-sm text-muted-foreground'>Failed to load file content</p>
          <Button type='button' variant='outline' onClick={handleOpenInNewTab}>
            <ExternalLink className='h-4 w-4 mr-2' />
            Open in New Tab
          </Button>
        </div>
      )}
    </div>
  )

  const renderOfficeDocumentViewer = () => (
    <div className='w-full h-[calc(80vh-100px)]'>
      {loading ? (
        <div className='flex flex-col justify-center items-center space-y-4 h-100'>
          <LoaderCircle className='h-8 w-8 animate-spin' />
          <p className='text-sm text-muted-foreground'>Loading document...</p>
        </div>
      ) : url ? (
        <div className='h-[calc(80vh-100px)] w-full'>
          <DocViewer
            documents={[
              {
                uri: url,
                fileType: fileExtension,
              },
            ]}
            pluginRenderers={DocViewerRenderers}
            style={{ height: '100%' }}
            config={{
              header: {
                disableHeader: true,
                disableFileName: true,
              },
            }}
          />
        </div>
      ) : (
        <div className='flex flex-col items-center justify-center h-64 space-y-4'>
          <div>{fileIconElement as React.ReactNode}</div>
          <p className='text-sm text-muted-foreground'>Failed to load document</p>
          <Button type='button' variant='outline' onClick={handleOpenInNewTab}>
            <ExternalLink className='h-4 w-4 mr-2' />
            Open in New Tab
          </Button>
        </div>
      )}
    </div>
  )

  const renderUnsupportedFileViewer = () => (
    <div className='flex flex-col items-center justify-center h-64 space-y-4'>
      <div className='flex items-center space-x-2'>
        <div>{fileIconElement as React.ReactNode}</div>
        <div className='text-center'>
          <h3 className='font-semibold'>{fileName}</h3>
          <p className='text-sm text-muted-foreground'>{mimeType}</p>
        </div>
      </div>
      <div className='flex space-x-2'>
        <Button type='button' variant='outline' onClick={handleOpenInNewTab}>
          <ExternalLink className='h-4 w-4 mr-2' />
          Open in New Tab
        </Button>
      </div>
    </div>
  )

  const renderViewer = () => {
    if (!url) return null

    if (isImage) return renderImageViewer()
    else if (isOfficeDoc) return renderOfficeDocumentViewer()
    else if (isPdf) return renderPdfViewer()
    else if (isTextFile) return renderTextFileViewer()
    else return renderUnsupportedFileViewer()
  }

  return (
    <Modal size='xl' open={open} setOpen={setOpen} nested={isFileViewerNestedModal}>
      <div className='flex pb-2 flex-wrap gap-2 items-center justify-between'>
        <div className='flex items-center space-x-3 '>
          <div>
            <h1 className='text-lg'>{title ?? fileName}</h1>
            <div className='flex items-center space-x-2 mt-1'>
              <div className='md:block hidden'>
                <Badge variant='secondary' className='text-xs'>
                  {mimeType.length > 100 ? `${mimeType.substring(0, 100)}...` : mimeType}
                </Badge>
              </div>
              <div className='block md:hidden'>
                <Badge variant='secondary' className='text-xs'>
                  {mimeType.length > 20 ? `${mimeType.substring(0, 20)}...` : mimeType}
                </Badge>
              </div>
              {fileExtension && (
                <Badge variant='outline' className='text-xs'>
                  {fileExtension.toUpperCase()}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className='flex items-center space-x-2'>
          {isImage && (
            <>
              <Button type='button' variant='outline' size='sm' onClick={zoomOut}>
                <ZoomOut className='h-4 w-4' />
              </Button>
              <Button type='button' variant='outline' size='sm' onClick={resetZoom}>
                <span className='text-xs'>{Math.round(imageScale * 100)}%</span>
              </Button>
              <Button type='button' variant='outline' size='sm' onClick={zoomIn}>
                <ZoomIn className='h-4 w-4' />
              </Button>
            </>
          )}
          <Button type='button' variant='outline' size='sm' onClick={() => setOpen(false)}>
            <X className='h-4 w-4' />
          </Button>
        </div>
      </div>

      <div className=' h-[calc(80vh-100px)] overflow-hidden '>{renderViewer()}</div>
    </Modal>
  )
}

export default URLViewerModal
